import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Sparkles, Trophy, X, AlertCircle, Users } from 'lucide-react';
import LoadMoreButton from '../components/LoadMoreButton';
import PageLoader from '../components/PageLoader';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import { linkify } from '../utils/linkify';
import type { Submission, Challenge, User } from '@crewcast/shared';
import { resolveAssets } from '@crewcast/shared';
import AssetLightbox, { type LightboxAsset } from '../components/AssetLightbox';
import '../styles/submissions.css';

interface LightboxState {
  assets: LightboxAsset[];
  index: number;
}

interface SubmissionWithDetails extends Submission {
  challengeTitle?: string;
  challengeDescription?: string;
  challengePoints?: number;
  submitterName?: string;
  submitterEmail?: string;
}

export default function ReviewSubmissionsPage() {
  const { token } = useAuth();
  const [submissions, setSubmissions] = useState<SubmissionWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const cursorRef = useRef<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<SubmissionWithDetails | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectFormError, setRejectFormError] = useState('');
  const [rejectSuccessMessage, setRejectSuccessMessage] = useState('');
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);

  const navigate = useNavigate();

  const openLightbox = (submission: Submission, startIndex = 0) => {
    const assets = resolveAssets(submission).map((a) => ({ url: a.url, type: a.type }));
    if (!assets.length) return;
    setLightbox({ assets, index: startIndex });
  };


  useEffect(() => {
    fetchPendingSubmissions();
  }, []);

  const refreshSidebarCounts = () => {
    window.dispatchEvent(new Event('crewcast:refresh-admin-counts'));
  };

  const closeRejectModal = () => {
    setRejectModalOpen(false);
    setRejectTarget(null);
    setRejectReason('');
    setRejectFormError('');
    setRejectSuccessMessage('');
  };

  const fetchPendingSubmissions = async (fetchCursor: string | null = null, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError('');
    }

    try {
      if (!token) throw new Error('Not authenticated');

      const url = new URL(`${authConfig.apiUrl}/api/admin/submissions/pending`);
      url.searchParams.set('limit', '20');
      if (fetchCursor) url.searchParams.set('cursor', fetchCursor);

      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch submissions');

      const data = await response.json();
      const nextCursor: string | null = data.nextCursor ?? null;

      // Enrich only the newly fetched batch with challenge and user details
      const enrichedSubmissions = await Promise.all(
        data.submissions.map(async (sub: Submission) => {
          const enriched: SubmissionWithDetails = { ...sub };

          try {
            const challengeResponse = await fetch(
              `${authConfig.apiUrl}/api/admin/challenges/${sub.challengeId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (challengeResponse.ok) {
              const challengeData = await challengeResponse.json();
              enriched.challengeTitle = challengeData.challenge.title;
              enriched.challengeDescription = challengeData.challenge.description;
              enriched.challengePoints = challengeData.challenge.points;
            }
          } catch (err) {
            console.error('Failed to fetch challenge:', err);
          }

          try {
            const userResponse = await fetch(
              `${authConfig.apiUrl}/api/admin/employees/${sub.userId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (userResponse.ok) {
              const userData = await userResponse.json();
              enriched.submitterName = `${userData.employee.firstName} ${userData.employee.lastName}`;
              enriched.submitterEmail = userData.employee.email;
            }
          } catch (err) {
            console.error('Failed to fetch user:', err);
          }

          return enriched;
        })
      );

      if (append) {
        setSubmissions((prev) => [...prev, ...enrichedSubmissions]);
      } else {
        setSubmissions(enrichedSubmissions);
      }

      cursorRef.current = nextCursor;
      setHasMore(nextCursor !== null);
      refreshSidebarCounts();
    } catch (err: any) {
      setError(err.message || 'Failed to load submissions');
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const loadMore = () => {
    if (!hasMore || loadingMore || loading) return;
    fetchPendingSubmissions(cursorRef.current, true);
  };

  const handleApprove = async (submissionId: string) => {
    const sub = submissions.find((s) => s.submissionId === submissionId);
    const hasPoints = (sub?.challengePoints ?? 0) > 0;
    const confirmMsg = hasPoints
      ? 'Approve this submission? Points will be awarded to all participants.'
      : 'Approve this submission?';
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setProcessingId(submissionId);
    setError('');

    try {
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/submissions/${submissionId}/approve`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to approve submission');
      }

      const data = await response.json();

      const quips = [
        "This one's ready for its close-up.",
        "Fresh content, locked and loaded.",
        "Head to the Content Vault to download and publish.",
        "One step closer to going viral.",
        "Your content library just got better.",
      ];
      const quip = quips[Math.floor(Math.random() * quips.length)];
      const pointsMsg = data.pointsAwarded > 0
        ? ` ${data.pointsAwarded} points awarded to ${data.participantsAwarded} participant(s).`
        : '';
      alert(`Submission approved! ${quip}${pointsMsg}`);

      // Remove from pending list
      setSubmissions((prev) => prev.filter((sub) => sub.submissionId !== submissionId));
      refreshSidebarCounts();
    } catch (err: any) {
      setError(err.message || 'Failed to approve submission');
      alert(`Error: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectModal = (submission: SubmissionWithDetails) => {
    setRejectTarget(submission);
    setRejectReason('');
    setRejectFormError('');
    setRejectSuccessMessage('');
    setRejectModalOpen(true);
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;

    const trimmed = rejectReason.trim();
    if (!trimmed) {
      setRejectFormError('Rejection reason is required.');
      return;
    }
    if (trimmed.length > 500) {
      setRejectFormError('Please keep the reason under 500 characters.');
      return;
    }

    setProcessingId(rejectTarget.submissionId);
    setRejectFormError('');
    setError('');

    try {
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/submissions/${rejectTarget.submissionId}/reject`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ rejectionReason: trimmed }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to reject submission');
      }

      // Update UI immediately
      setSubmissions((prev) =>
        prev.filter((sub) => sub.submissionId !== rejectTarget.submissionId)
      );
      refreshSidebarCounts();

      setRejectSuccessMessage('Submission rejected. Reason sent to employee.');
      // Auto-close after a short delay
      setTimeout(() => closeRejectModal(), 1200);
    } catch (err: any) {
      setRejectFormError(err.message || 'Failed to reject submission');
    } finally {
      setProcessingId(null);
    }
  };

  const pendingCount = submissions.filter((s) => s.approved === null).length;

  if (loading) {
    return (
      <div className="review-submissions-page">
        <PageLoader />
      </div>
    );
  }

  return (
    <div className="review-submissions-page">
      <div className="review-page-header">
        <div>
          <h1>Review Submissions</h1>
          <p className="page-subtitle">Review and approve employee submissions</p>
        </div>
        {pendingCount > 0 && (
          <div className="pending-badge-large">
            {pendingCount} Pending
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {submissions.length === 0 ? (
        <div className="no-submissions">
          <div className="no-submissions-icon">
            <Sparkles size={48} color="#00bf62" />
          </div>
          <h2>All caught up!</h2>
          <p>No pending submissions to review.</p>
        </div>
      ) : (
        <div className="review-submissions-grid">
          {submissions.map((submission) => (
            <div key={submission.submissionId} className="review-submission-card">
              {/* Media Preview */}
              <div className="review-submission-media" style={{ position: 'relative' }}>
                {(() => {
                  const assets = resolveAssets(submission);
                  const primary = assets[0];
                  return (
                    <>
                      {primary.type === 'video' ? (
                        <video src={`${primary.url}#t=0.001`} className="review-media" muted playsInline preload="metadata" />
                      ) : (
                        <img src={primary.url} alt="Submission" className="review-media" />
                      )}
                      {assets.length > 1 && (
                        <span className="submission-asset-count-badge">
                          +{assets.length - 1} more
                        </span>
                      )}
                    </>
                  );
                })()}
                <button
                  className="media-tap-target"
                  onClick={() => openLightbox(submission, 0)}
                  aria-label="View submission"
                />
              </div>

              {/* Submission Details */}
              <div className="review-submission-content">
                {/* Challenge Info */}
                {submission.challengeTitle && (
                  <div className="review-challenge-info">
                    <div>
                      <h3
                        className="review-challenge-title"
                        onClick={() => navigate(`/moments/${submission.challengeId}`)}
                      >
                        {submission.challengeTitle}
                      </h3>
                      {submission.challengeDescription && (
                        <p className="review-challenge-description">{linkify(submission.challengeDescription)}</p>
                      )}
                    </div>
                    {(submission.challengePoints ?? 0) > 0 && (
                      <span className="review-points" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Trophy size={16} /> {submission.challengePoints} points
                      </span>
                    )}
                  </div>
                )}

                {/* Submitter Info */}
                {submission.submitterName && (
                  <div className="review-submitter-info">
                    <div className="submitter-avatar">
                      {submission.submitterName.charAt(0)}
                    </div>
                    <div className="submitter-details">
                      <div className="submitter-name">{submission.submitterName}</div>
                      <div className="submitter-email">{submission.submitterEmail}</div>
                    </div>
                  </div>
                )}

                {/* Caption */}
                {submission.caption && (
                  <div className="review-caption">
                    <strong>Caption:</strong>
                    <p>{submission.caption}</p>
                  </div>
                )}

                {/* Metadata */}
                <div className="review-metadata">
                  <div className="metadata-row">
                    <span className="metadata-label">Submitted:</span>
                    <span className="metadata-value">
                      {new Date(submission.submittedAt).toLocaleDateString()} at{' '}
                      {new Date(submission.submittedAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  {submission.collaborators && submission.collaborators.length > 0 && (
                    <div className="metadata-row">
                      <span className="metadata-label">
                        Collaborator{submission.collaborators.length > 1 ? 's' : ''}:
                      </span>
                      <span className="metadata-value">
                        <Users size={14} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
                        {submission.collaboratorNames && submission.collaboratorNames.length > 0
                          ? submission.collaboratorNames.join(', ')
                          : `${submission.collaborators.length} collaborator${submission.collaborators.length > 1 ? 's' : ''}`}
                      </span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="review-actions">
                  <button
                    onClick={() => handleApprove(submission.submissionId)}
                    disabled={processingId === submission.submissionId}
                    className="approve-btn"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                  >
                    {processingId === submission.submissionId
                      ? 'Processing...'
                      : <><CheckCircle size={18} /> Approve</>}
                  </button>
                  <button
                    onClick={() => openRejectModal(submission)}
                    disabled={processingId === submission.submissionId}
                    className="reject-btn"
                  >
                    {processingId === submission.submissionId
                      ? 'Processing...'
                      : '❌ Reject'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <LoadMoreButton onClick={loadMore} loading={loadingMore} hasMore={hasMore} />

      {lightbox && (
        <AssetLightbox
          assets={lightbox.assets}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onIndexChange={(i) => setLightbox((lb) => lb ? { ...lb, index: i } : lb)}
        />
      )}

      {rejectModalOpen && (
        <div
          className="crewcast-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reject-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && processingId === null) closeRejectModal();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && processingId === null) closeRejectModal();
          }}
          tabIndex={-1}
        >
          <div className="crewcast-modal-card">
            <div className="crewcast-modal-header">
              <div>
                <h2 id="reject-modal-title" className="crewcast-modal-title">
                  Reject submission
                </h2>
                {rejectTarget?.challengeTitle && (
                  <div className="crewcast-modal-subtitle">
                    {rejectTarget.challengeTitle}
                    {rejectTarget.submitterName ? ` • ${rejectTarget.submitterName}` : ''}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="crewcast-modal-close"
                onClick={() => (processingId === null ? closeRejectModal() : null)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="crewcast-modal-body">
              <div className="crewcast-modal-hint">
                <AlertCircle size={16} />
                <span>
                  Required: add a clear, actionable reason the employee can fix.
                </span>
              </div>

              <label className="crewcast-modal-label" htmlFor="rejectionReason">
                Rejection reason
              </label>
              <textarea
                id="rejectionReason"
                className="crewcast-modal-textarea"
                placeholder="e.g. Video is too dark, please re-upload with better lighting."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                maxLength={500}
                rows={5}
                disabled={processingId !== null}
                autoFocus
              />
              <div className="crewcast-modal-charcount">
                {rejectReason.trim().length}/500
              </div>

              {rejectFormError && (
                <div className="crewcast-modal-error">{rejectFormError}</div>
              )}

              {rejectSuccessMessage && (
                <div className="crewcast-modal-success">
                  ✓ {rejectSuccessMessage}
                </div>
              )}
            </div>

            <div className="crewcast-modal-actions">
              <button
                type="button"
                className="crewcast-modal-btn secondary"
                onClick={() => (processingId === null ? closeRejectModal() : null)}
                disabled={processingId !== null}
              >
                Cancel
              </button>
              <button
                type="button"
                className="crewcast-modal-btn danger"
                onClick={confirmReject}
                disabled={processingId !== null}
              >
                {processingId ? 'Rejecting…' : 'Reject submission'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

